
























































































































































































import {
  InstrumentDetail,
  InstrumentPerformanceData, RiskAndVolatilityMeasures,
} from "@/shared/types";
import { Component, Prop, Vue, Watch } from "@/shared/vuepack";
import moment from "moment";
import _ from "lodash";
import { roundToTwoDecimals } from "@/shared/mathHelper";
import { formatCurrency } from "@/shared/currency";
import { useDrivewealthStore } from "@/shared/store/drivewealth";
import { useInstrumentPerformanceDataStore } from "@/shared/store/instrumentPerformanceData";

enum CountryAllocationTableType {
  Allocation,
  Country
}

enum TimePeriod {
  OneYear = "M12",
  ThreeYear = "M36",
  FiveYear = "M60"
}

enum SectorType {
  Bond = "bond",
  Stock = "stock",
}

@Component({
  computed: {
    CountryAllocationTableType () {
      return CountryAllocationTableType;
    }
  }
})
export default class EtfPerformanceSection extends Vue {
  @Prop({ required: false }) username: string;
  @Prop({ required: false }) partnerId: number;
  @Prop() symbol: string;
  @Prop() accountId: number;

  moment = moment;
  formatCurrency = formatCurrency;
  roundToTwoDecimals = roundToTwoDecimals;

  instrumentPerformanceDataStore = useInstrumentPerformanceDataStore();
  drivewealthStore = useDrivewealthStore();

  collapseExpanded = false;
  selectedCountryAllocationTable = CountryAllocationTableType.Allocation;
  selectedTimePeriod = TimePeriod.OneYear;
  selectedSector = null;

  countryAllocationTableOptions = [
    {
      text: "Asset Allocation",
      value: CountryAllocationTableType.Allocation,
    },
    {
      text: "Country",
      value: CountryAllocationTableType.Country,
    }
  ];

  timePeriodOptions = [
    {
      text: "1Y",
      value: TimePeriod.OneYear,
    },
    {
      text: "3Y",
      value: TimePeriod.ThreeYear,
    },
    {
      text: "5Y",
      value: TimePeriod.FiveYear,
    }
  ];

  mounted (): void {
    this.selectedSector = this.hasSectorAllocations ? this.sectorOptions[0].value : SectorType.Stock;
  }

  get sectorOptions (): {text: string; value: SectorType}[] {
    let sectorSelectOptions = [];

    if (this.hasStockSectorAllocations) {
      sectorSelectOptions = [{text: "Stock Sector Allocation", value: SectorType.Stock}];
    }

    if (this.hasBondSectorAllocations) {
      sectorSelectOptions = [
          ...sectorSelectOptions,
          {text: "Bond Sector Allocation", value: SectorType.Bond}
      ];
    }

    return sectorSelectOptions;
  }

  get instrumentDetail (): InstrumentDetail {
    return this.drivewealthStore.instrumentDetailBySymbol(this.symbol);
  }

  get isLoaded (): boolean {
    return this.instrumentPerformanceDataStore.instrumentPerformanceLoaded(this.symbol);
  }

  get instrumentPerformanceData (): InstrumentPerformanceData {
    return this.instrumentPerformanceDataStore.instrumentPerformanceData(this.symbol);
  }

  get hasSectorAllocations (): boolean {
    return this.hasStockSectorAllocations || this.hasBondSectorAllocations;
  }

  get hasStockSectorAllocations (): boolean {
    return this.stockSectorAllocations.length > 0;
  }

  get hasBondSectorAllocations (): boolean {
    return this.bondSectorAllocations.length > 0;
  }

  get sectorAllocationTableFields (): { key: string; label: string }[] {
    return [
      { key: "definition", label: "Sectors" },
      { key: "investmentPercentage", label: "Investment" }
    ];
  }

  get selectedSectorAllocations () {
    return this.selectedSector === SectorType.Stock ?
        this.stockSectorAllocations :
        this.bondSectorAllocations;
  }

  get stockSectorAllocations () {
    if (!this.instrumentPerformanceData.stockSectorBreakdown) {
      return [];
    }

    const tableItems = this.instrumentPerformanceData.stockSectorBreakdown.map(x => {
      return {
        definition: x.definition,
        investmentPercentage: `${roundToTwoDecimals(x.investmentPercentage).toFixed(2)}%`,
      };
    });

    return _.take(tableItems, 10);
  }

  get bondSectorAllocations () {
    if (!this.instrumentPerformanceData.bondSectorBreakdown) {
      return [];
    }

    const tableItems = this.instrumentPerformanceData.bondSectorBreakdown.map(x => {
      return {
        definition: x.definition,
        investmentPercentage: `${roundToTwoDecimals(x.investmentPercentage).toFixed(2)}%`,
      };
    });

    return _.take(tableItems, 10);
  }

  get assetAllocationTableFields (): { key: string; label: string }[] {
    return [
      { key: "definition", label: "Asset Class" },
      { key: "investmentPercentage", label: "Investment %" }
    ];
  }

  get assetAllocations () {
    return this.instrumentPerformanceData.assetAllocation.map(x => {
      return {
        definition: x.definition,
        investmentPercentage: `${roundToTwoDecimals(x.investmentPercentage).toFixed(2)}%`,
      };
    });
  }

  get countryExposureTableFields (): { key: string; label: string }[] {
    return [
      { key: "definition", label: "Country" },
      { key: "investmentPercentage", label: "Investment %" }
    ];
  }

  get countryExposures () {
    return this.instrumentPerformanceData.countryExposure.map(x => {
      return {
        definition: x.definition,
        investmentPercentage: `${roundToTwoDecimals(x.investmentPercentage).toFixed(2)}%`,
      };
    });
  }

  get selectedRiskData (): RiskAndVolatilityMeasures {
    return _.find(this.instrumentPerformanceData.riskAndVolatilityMeasures, x => x.timePeriod === this.selectedTimePeriod);
  }

  get panelToggleCopy (): string {
    return this.collapseExpanded ? "- show less" : "+ show more";
  }

  togglePanelDisplay (): void {
    this.collapseExpanded = !this.collapseExpanded;
  }

  setSelectedCountryAllocationTableDisplay (tableType: CountryAllocationTableType): void {
    this.selectedCountryAllocationTable = tableType;
  }

  setSelectedTimePeriodTableDisplay (timePeriod: TimePeriod): void {
    this.selectedTimePeriod = timePeriod;
  }

  setSelectedSectorTableDisplay (sector: SectorType): void {
    this.selectedSector = sector;
  }

  getIconStyleForIndex (index: number): string {
    const rating = this.parsedStarRating(this.instrumentPerformanceData.morningstarRating3Y);

    return index <= rating ? "fa-solid" : "fa-regular";
  }

  parsedStarRating (valueAsString: string): number {
    return parseInt(valueAsString) || 0;
  }
}
